<template>
  <div class="my-ad-detail-page">
    <!-- 페이지 헤더 -->
    <div class="page-header">
      <button class="back-btn" @click="goBack" title="뒤로가기">←</button>
      <h1 class="page-title">{{ campaign?.material?.title || "광고 상세" }}</h1>
    </div>

    <div v-if="loading" class="state-block">
      <div class="spinner"></div>
      <p class="state-desc">캠페인 정보를 불러오는 중...</p>
    </div>

    <div v-else-if="errorMessage" class="state-block">
      <p class="state-error">{{ errorMessage }}</p>
      <button class="primary-btn" @click="fetchAll">다시 시도</button>
    </div>

    <template v-else-if="campaign">
      <!-- 캠페인 카드 -->
      <section class="card">
        <div class="card-row">
          <div class="card-thumb">
            <img v-if="campaign.material?.imageUrl" :src="campaign.material.imageUrl" alt="광고 이미지" />
            <div v-else class="card-thumb-placeholder">이미지 없음</div>
          </div>
          <div class="card-main">
            <div class="card-top">
              <span class="plan-name">{{ campaign.planSnapshot?.name || "-" }}</span>
              <span class="status-badge" :class="`status-${campaign.status}`">{{ statusLabel(campaign.status) }}</span>
            </div>
            <div class="card-line">
              광고주: {{ campaign.advertiser?.companyName || "-" }}
              <span class="dot">·</span>
              담당자: {{ campaign.advertiser?.contactName || "-" }}
            </div>
            <div v-if="campaign.status === 'rejected' && campaign.rejectReason" class="reject-box">
              <strong>반려 사유:</strong> {{ campaign.rejectReason }}
            </div>
            <div v-if="campaign.lastPaymentStatus === 'failed'" class="warn-box">
              ⚠ 최근 결제에 실패하였습니다. 카카오톡 「링꾹」 채널로 문의해주세요.
            </div>
            <div class="card-meta">
              <div>
                <span class="meta-label">다음 결제일</span>
                <span class="meta-value">{{ formatDate(campaign.nextBillingAt) || "-" }}</span>
              </div>
              <div>
                <span class="meta-label">월 결제 금액</span>
                <span class="meta-value">{{ formatWon(campaign.planSnapshot?.totalAmount) }}</span>
              </div>
              <div>
                <span class="meta-label">등록 카드</span>
                <span class="meta-value">
                  {{ campaign.billing?.cardCompany || "" }}
                  {{ campaign.billing?.cardNumberMasked || "정보 없음" }}
                </span>
              </div>
            </div>
            <div v-if="canCancel" class="card-actions">
              <button class="danger-outline" @click="onCancelClick" :disabled="canceling">
                {{ canceling ? "처리 중..." : "정기결제 해지" }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- 성과 -->
      <section class="card">
        <div class="card-header">
          <h2 class="card-title">📊 광고 성과 (간이 보고서)</h2>
          <span class="card-sub">{{ metricsPeriodLabel }}</span>
        </div>

        <div v-if="metricsLoading" class="inline-loading">
          <div class="spinner"></div>
        </div>
        <div v-else-if="!metrics" class="inline-empty">
          아직 집계된 성과 데이터가 없습니다.
        </div>
        <template v-else>
          <div class="metric-grid">
            <div class="metric-card">
              <div class="metric-icon">👆</div>
              <div class="metric-label">총 클릭</div>
              <div class="metric-value">{{ (metrics.totalClicks || 0).toLocaleString() }}회</div>
            </div>
            <div class="metric-card metric-highlight">
              <div class="metric-icon">📈</div>
              <div class="metric-label">추정 클릭률</div>
              <div class="metric-value">{{ formatCTR(metrics.ctr) }}%</div>
            </div>
            <div class="metric-card">
              <div class="metric-icon">👁️</div>
              <div class="metric-label">총 노출</div>
              <div class="metric-value">{{ (metrics.totalImpressions || 0).toLocaleString() }}회</div>
            </div>
          </div>

          <div class="chart-wrapper">
            <Chart type="bar" :data="monthlyChartData" :options="chartOptions" class="chart" />
          </div>
          <p class="chart-note">* 추정 클릭률 = (총 클릭 / 총 노출) × 100</p>
        </template>
      </section>

      <!-- 결제 내역 -->
      <section class="card">
        <div class="card-header">
          <h2 class="card-title">🧾 결제 내역</h2>
        </div>
        <div v-if="paymentsLoading" class="inline-loading">
          <div class="spinner"></div>
        </div>
        <div v-else-if="payments.length === 0" class="inline-empty">
          결제 내역이 없습니다.
        </div>
        <ul v-else class="payment-list">
          <li v-for="p in payments" :key="p.id" class="payment-row">
            <div class="payment-main">
              <span class="payment-date">{{ formatDate(p.paidAt) || "-" }}</span>
              <span class="payment-method">
                {{ p.cardCompany || "카드" }} {{ p.cardNumberMasked || "" }}
              </span>
            </div>
            <div class="payment-amount">
              <span class="payment-status" :class="paymentStatusClass(p.status)">{{ paymentStatusLabel(p.status) }}</span>
              <strong>{{ formatWon(p.totalAmount) }}</strong>
            </div>
          </li>
        </ul>
      </section>

      <!-- 광고 소재 -->
      <section class="card">
        <div class="card-header">
          <h2 class="card-title">🎨 광고 소재</h2>
        </div>
        <div class="material-preview">
          <img v-if="campaign.material?.imageUrl" :src="campaign.material.imageUrl" alt="광고 배너" />
        </div>
        <div class="material-info">
          <div class="material-row">
            <span class="material-label">광고 제목</span>
            <span class="material-value">{{ campaign.material?.title || "-" }}</span>
          </div>
          <div class="material-row">
            <span class="material-label">클릭 시 이동 URL</span>
            <span class="material-value">
              <a v-if="campaign.material?.link" :href="campaign.material.link" target="_blank" rel="noopener">
                {{ campaign.material.link }}
              </a>
              <template v-else>-</template>
            </span>
          </div>
          <div v-if="campaign.material?.description" class="material-row">
            <span class="material-label">설명</span>
            <span class="material-value">{{ campaign.material.description }}</span>
          </div>
        </div>
        <p class="material-note">
          소재 변경은 카카오톡 「링꾹」 채널로 요청해주세요.
        </p>
      </section>
    </template>

    <!-- 해지 확인 모달 -->
    <div v-if="cancelDialogVisible" class="confirm-overlay" @click.self="cancelDialogVisible = false">
      <div class="confirm-dialog">
        <h3>정기결제를 해지하시겠습니까?</h3>
        <p>
          해지 후에도 이미 결제된 당월 분(<strong>{{ formatDate(campaign?.currentPeriodEnd) }}</strong> 까지)은 노출이 유지됩니다.
          이후 자동 결제가 중단되며 광고가 종료됩니다.
        </p>
        <p class="cancel-policy-note">{{ cancelRefundSummary }}</p>
        <textarea v-model="cancelReason" rows="3" placeholder="해지 사유 (선택)" maxlength="200" />
        <div class="confirm-actions">
          <button class="secondary-btn" @click="cancelDialogVisible = false" :disabled="canceling">취소</button>
          <button class="danger-btn" @click="onConfirmCancel" :disabled="canceling">
            {{ canceling ? "처리 중..." : "해지하기" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "primevue/chart";
import {
  getAdCampaign,
  listAdCampaignPayments,
  getAdCampaignMetrics,
  cancelAdSubscription,
  formatWon,
  AD_CANCEL_REFUND_SUMMARY,
} from "@/utils/adApplication";

const STATUS_LABELS = {
  draft: "임시저장",
  pending_payment: "결제 진행 중",
  pending_review: "심사 대기",
  active: "노출 중",
  paused: "일시정지",
  rejected: "반려",
  canceled: "해지 예정",
  expired: "종료",
};

export default {
  name: "MyAdDetail",
  components: { Chart },
  data() {
    return {
      loading: true,
      errorMessage: "",
      campaign: null,
      payments: [],
      paymentsLoading: false,
      metrics: null,
      metricsLoading: false,
      canceling: false,
      cancelDialogVisible: false,
      cancelReason: "",
      cancelRefundSummary: AD_CANCEL_REFUND_SUMMARY,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom", labels: { font: { size: 11 } } },
        },
        scales: {
          y: { beginAtZero: true, ticks: { font: { size: 11 } } },
          x: { ticks: { font: { size: 11 } } },
        },
      },
    };
  },
  computed: {
    campaignId() {
      return String(this.$route.params.id || "").trim();
    },
    canCancel() {
      const status = this.campaign?.status;
      return status === "active" || status === "pending_review" || status === "paused";
    },
    monthlyChartData() {
      const monthly = Array.isArray(this.metrics?.monthly) ? this.metrics.monthly : [];
      return {
        labels: monthly.map((m) => m.yearMonth || ""),
        datasets: [
          {
            label: "노출",
            data: monthly.map((m) => Number(m.impressions) || 0),
            backgroundColor: "rgba(102, 189, 255, 0.55)",
            borderRadius: 6,
          },
          {
            label: "클릭",
            data: monthly.map((m) => Number(m.clicks) || 0),
            backgroundColor: "rgba(255, 153, 102, 0.85)",
            borderRadius: 6,
          },
        ],
      };
    },
    metricsPeriodLabel() {
      const start = this.metrics?.currentPeriod?.start;
      const end = this.metrics?.currentPeriod?.end;
      if (start && end) return `${this.formatDate(start)} ~ ${this.formatDate(end)}`;
      const days = this.metrics?.periodDays;
      if (days) return `최근 ${days}일`;
      return "전체 누적";
    },
  },
  mounted() {
    if (!this.campaignId) {
      this.errorMessage = "캠페인 정보가 없습니다.";
      this.loading = false;
      return;
    }
    this.fetchAll();
  },
  methods: {
    formatWon,
    async fetchAll() {
      try {
        this.loading = true;
        this.errorMessage = "";
        const campaign = await getAdCampaign(this.campaignId);
        this.campaign = campaign;
        if (!campaign) {
          this.errorMessage = "캠페인을 찾을 수 없습니다.";
          return;
        }
        this.loading = false;
        this.fetchPayments();
        this.fetchMetrics();
      } catch (e) {
        console.error("캠페인 상세 로드 실패", e);
        this.errorMessage = e?.response?.data?.message || e?.message || "캠페인 정보를 불러오지 못했습니다.";
        this.loading = false;
      }
    },
    async fetchPayments() {
      try {
        this.paymentsLoading = true;
        this.payments = await listAdCampaignPayments(this.campaignId);
      } catch (e) {
        console.warn("결제 내역 로드 실패", e);
        this.payments = [];
      } finally {
        this.paymentsLoading = false;
      }
    },
    async fetchMetrics() {
      try {
        this.metricsLoading = true;
        this.metrics = await getAdCampaignMetrics(this.campaignId);
      } catch (e) {
        console.warn("성과 데이터 로드 실패", e);
        this.metrics = null;
      } finally {
        this.metricsLoading = false;
      }
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || "임시저장";
    },
    paymentStatusLabel(status) {
      if (status === "paid") return "결제 완료";
      if (status === "failed") return "결제 실패";
      if (status === "refunded") return "환불";
      return "";
    },
    paymentStatusClass(status) {
      if (status === "paid") return "ps-paid";
      if (status === "failed") return "ps-failed";
      if (status === "refunded") return "ps-refunded";
      return "";
    },
    formatCTR(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "0.00";
      return n.toFixed(2);
    },
    formatDate(value) {
      if (!value) return "";
      const raw = value._seconds
        ? new Date(value._seconds * 1000)
        : new Date(value);
      if (Number.isNaN(raw.getTime())) return "";
      return `${raw.getFullYear()}.${String(raw.getMonth() + 1).padStart(2, "0")}.${String(raw.getDate()).padStart(2, "0")}`;
    },
    goBack() {
      this.$router.push("/my-ads");
    },
    onCancelClick() {
      this.cancelReason = "";
      this.cancelDialogVisible = true;
    },
    async onConfirmCancel() {
      try {
        this.canceling = true;
        await cancelAdSubscription(this.campaignId, this.cancelReason.trim());
        this.cancelDialogVisible = false;
        this.$alert?.("정기결제가 해지되었습니다. 현재 결제 기간이 끝날 때까지는 광고가 노출됩니다.");
        await this.fetchAll();
      } catch (e) {
        console.error("해지 실패", e);
        this.$alert?.(e?.response?.data?.message || e?.message || "해지 처리에 실패했습니다.");
      } finally {
        this.canceling = false;
      }
    },
  },
};
</script>

<style scoped>
.my-ad-detail-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 18px 80px;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.back-btn {
  background: transparent;
  border: none;
  font-size: 20px;
  color: #495057;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.back-btn:hover { background: #f1f3f5; }

.page-title {
  flex: 1;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 18px;
  color: #2d3748;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 18px;
}

.card-row {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 18px;
}

.card-thumb {
  width: 160px;
  height: 110px;
  border-radius: 8px;
  background: #f1f3f5;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-thumb img { width: 100%; height: 100%; object-fit: cover; }
.card-thumb-placeholder { color: #adb5bd; font-size: 12px; }

.card-main {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.card-top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.plan-name {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 16px;
  color: #2d3748;
}

.status-badge {
  padding: 3px 10px;
  border-radius: 999px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 11px;
}

.status-draft { background: #f1f3f5; color: #495057; }
.status-pending_payment { background: #fff3bf; color: #5c3c00; }
.status-pending_review { background: #fff3bf; color: #5c3c00; }
.status-active { background: #d3f9d8; color: #2b8a3e; }
.status-paused { background: #ffe3e3; color: #c92a2a; }
.status-rejected { background: #ffe3e3; color: #c92a2a; }
.status-canceled { background: #e9ecef; color: #495057; }
.status-expired { background: #e9ecef; color: #495057; }

.card-line {
  font-size: 13px;
  color: #495057;
}

.dot { color: #ced4da; margin: 0 4px; }

.reject-box {
  background: #ffe3e3;
  color: #c92a2a;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.warn-box {
  background: #fff3bf;
  color: #5c3c00;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.card-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px dashed #e9ecef;
}

.card-meta > div { display: flex; flex-direction: column; gap: 4px; }

.meta-label { font-size: 11px; color: #868e96; font-family: "NanumSquareRoundB", sans-serif; }
.meta-value { font-size: 13px; color: #212529; font-family: "NanumSquareRoundB", sans-serif; }

.card-actions {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
}

.danger-outline {
  background: #fff;
  color: #c92a2a;
  border: 1px solid #ffc9c9;
  padding: 8px 14px;
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
  cursor: pointer;
}

.danger-outline:hover { background: #fff5f5; }
.danger-outline:disabled { opacity: 0.6; cursor: not-allowed; }

/* 카드 헤더 (성과/결제내역/소재 공통) */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.card-title {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 15px;
  color: #2d3748;
  margin: 0;
}

.card-sub {
  font-size: 12px;
  color: #868e96;
}

/* 메트릭 */
.metric-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.metric-card {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
}

.metric-highlight {
  background: linear-gradient(135deg, #e7f5ff 0%, #d0ebff 100%);
}

.metric-icon { font-size: 20px; }
.metric-label { font-size: 11px; color: #495057; }
.metric-value {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 18px;
  color: #1c7ed6;
}

.chart-wrapper {
  height: 240px;
}

.chart { width: 100%; height: 100%; }

.chart-note {
  margin: 8px 0 0;
  font-size: 11px;
  color: #868e96;
  text-align: right;
}

/* 결제 내역 */
.payment-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
}

.payment-row:last-child { border-bottom: none; }

.payment-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.payment-date { font-family: "NanumSquareRoundB", sans-serif; font-size: 13px; color: #212529; }
.payment-method { font-size: 12px; color: #6c757d; }

.payment-amount {
  display: flex;
  align-items: center;
  gap: 10px;
}

.payment-amount strong { font-family: "NanumSquareRoundEB", sans-serif; font-size: 14px; color: #212529; }

.payment-status {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.ps-paid { background: #d3f9d8; color: #2b8a3e; }
.ps-failed { background: #ffe3e3; color: #c92a2a; }
.ps-refunded { background: #e9ecef; color: #495057; }

/* 소재 */
.material-preview {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f3f5;
  margin-bottom: 14px;
}

.material-preview img {
  width: 100%;
  height: auto;
  max-height: 320px;
  object-fit: contain;
}

.material-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.material-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px;
  font-size: 13px;
}

.material-label { color: #6c757d; }
.material-value { color: #212529; overflow-wrap: anywhere; }

.material-note {
  margin: 14px 0 0;
  font-size: 11px;
  color: #868e96;
}

/* 상태 */
.state-block { text-align: center; padding: 80px 20px; }
.state-desc { color: #4a5568; font-size: 14px; }
.state-error { color: #c53030; font-size: 14px; margin-bottom: 16px; }
.spinner {
  width: 44px;
  height: 44px;
  border: 4px solid #e2e8f0;
  border-top-color: #66bdff;
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.inline-loading, .inline-empty {
  padding: 40px 0;
  text-align: center;
  color: #868e96;
  font-size: 13px;
}

.inline-loading .spinner { width: 32px; height: 32px; margin: 0 auto; }

.primary-btn {
  background: #66bdff;
  color: #fff;
  border: none;
  padding: 12px 22px;
  border-radius: 8px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 14px;
  cursor: pointer;
}
.primary-btn:hover { background: #4da3ff; }

/* 확인 모달 */
.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}

.confirm-dialog {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.confirm-dialog h3 { margin: 0; font-family: "NanumSquareRoundEB", sans-serif; font-size: 17px; color: #2d3748; }
.confirm-dialog p { margin: 0; font-size: 13px; color: #495057; line-height: 1.6; }

.cancel-policy-note {
  font-size: 12px;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 10px 12px;
  line-height: 1.6;
}

.confirm-dialog textarea {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 13px;
  resize: vertical;
  min-height: 70px;
}

.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.secondary-btn {
  padding: 10px 18px;
  background: #f1f3f5;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  cursor: pointer;
}

.danger-btn {
  padding: 10px 18px;
  background: #c92a2a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 13px;
  cursor: pointer;
}

.danger-btn:hover { background: #a51d1d; }
.danger-btn:disabled, .secondary-btn:disabled { opacity: 0.6; cursor: progress; }

@media (max-width: 720px) {
  .card-row {
    grid-template-columns: 1fr;
  }
  .card-thumb {
    width: 100%;
    height: 160px;
  }
  .card-meta {
    grid-template-columns: 1fr;
  }
  .metric-grid {
    grid-template-columns: 1fr;
  }
  .material-row {
    grid-template-columns: 1fr;
  }
}
</style>
